import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import { axiosInstance } from "../../axiosconfing";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_INTERVAL_MS = 60000;
const MAX_POLLS = 200;

const statusColor = {
  pending: "warning",
  processing: "info",
  active: "info",
  waiting: "warning",
  completed: "success",
  success: "success",
  failed: "error",
  error: "error",
};

const Invoices = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Date selection state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Preview table state
  const [invoiceData, setInvoiceData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  
  // Export state
  const [loadingExport, setLoadingExport] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState("");
  
  // Summary stats (cumulative for date range)
  const [summaryStats, setSummaryStats] = useState({
    totalInvoices: 0,
    totalBase: 0,
    totalWithGST: 0,
    totalGST: 0
  });
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const clearPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    pollCount.current = 0;
  };

  // Fetch cumulative summary stats for date range
  const fetchInvoiceSummary = async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoadingSummary(true);
      const res = await axiosInstance.get("/admin/invoice/summary", {
        params: {
          startDate: dayjs(startDate).format("YYYY-MM-DD"),
          endDate: dayjs(endDate).format("YYYY-MM-DD"),
        },
      });
      
      const data = res.data?.data || res.data || {};
      setSummaryStats({
        totalInvoices: data.totalInvoices || 0,
        totalBase: data.totalBase || 0,
        totalWithGST: data.totalWithGST || 0,
        totalGST: data.totalGST || 0
      });
    } catch (err) {
      console.error("Failed to fetch invoice summary:", err);
      setSummaryStats({
        totalInvoices: 0,
        totalBase: 0,
        totalWithGST: 0,
        totalGST: 0
      });
    } finally {
      setLoadingSummary(false);
    }
  };

  // Clear everything when dates change
  useEffect(() => {
    clearPolling();
    setJobStatus(null);
    setError("");
    setLoadingExport(false);
    setInvoiceData([]);
    setPagination({ page: 0, limit: 10, total: 0, totalPages: 0 });
    if (startDate && endDate) {
      fetchInvoiceSummary();
    }
  }, [startDate, endDate]);

  const triggerDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "invoices.zip");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pollStatus = (jobId) => {
    let currentInterval = POLL_INTERVAL_MS;
    pollCount.current = 0;

    const poll = async () => {
      try {
        const res = await axiosInstance.get(`/admin/invoice/download/status/${jobId}`);
        const responseData = res.data;
        const status = (responseData?.jobStatus || responseData?.status || "").toString().toLowerCase().trim();
        const downloadUrl = responseData?.downloadUrl;

        setJobStatus({ id: jobId, status, downloadUrl, msg: responseData?.msg });

        if (status === "completed" || status === "success") {
          clearPolling();
          setLoadingExport(false);
          if (downloadUrl) triggerDownload(downloadUrl);
          return;
        } else if (status === "failed" || status === "error") {
          clearPolling();
          setLoadingExport(false);
          return;
        }

        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          clearPolling();
          setLoadingExport(false);
          setError("Invoice generation timed out. Please try again.");
          return;
        }

        currentInterval = Math.min(currentInterval * 1.5, MAX_POLL_INTERVAL_MS);
        pollRef.current = setTimeout(poll, currentInterval);
      } catch (err) {
        currentInterval = Math.min(currentInterval * 1.5, MAX_POLL_INTERVAL_MS);
        pollRef.current = setTimeout(poll, currentInterval);
      }
    };
    poll();
  };

  // Fetch invoice preview
  const fetchInvoicePreview = async (page = 0, limitOverride = null) => {
    if (!startDate || !endDate) {
      setError("Please select both start date and end date.");
      return;
    }
    
    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      setError("End date cannot be before start date.");
      return;
    }

    setError("");
    setLoadingPreview(true);
    
    try {
      const res = await axiosInstance.get("/admin/invoice", {
        params: {
          startDate: dayjs(startDate).format("YYYY-MM-DD"),
          endDate: dayjs(endDate).format("YYYY-MM-DD"),
          page: page + 1, // Backend uses 1-based pagination
          limit: limitOverride ?? pagination.limit,
        },
      });

      // Backend returns: { status, data: [...], pagination: {...} }
      const responseData = res.data;
      const invoices = responseData?.data || [];
      const paginationData = responseData?.pagination || {};

      setInvoiceData(invoices);
      setPagination({
        page,
        limit: paginationData.limit || 10,
        total: paginationData.total || 0,
        totalPages: paginationData.totalPages || 0,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch invoices. Please try again.";
      setError(msg);
      setInvoiceData([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start date and end date.");
      return;
    }
    
    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      setError("End date cannot be before start date.");
      return;
    }

    setError("");
    setJobStatus(null);
    clearPolling();
    setLoadingExport(true);
    
    try {
      const res = await axiosInstance.post("/admin/invoice/download", {
        startDate: dayjs(startDate).format("YYYY-MM-DD"),
        endDate: dayjs(endDate).format("YYYY-MM-DD"),
      });

      const data = res.data?.data || res.data;
      const jobId = data?.id || data?.jobId;
      const status = (data?.jobStatus || data?.status || "pending").toLowerCase();

      setJobStatus({ id: jobId, status, ...data });

      if (status === "completed" && data?.downloadUrl) {
        triggerDownload(data.downloadUrl);
        setLoadingExport(false);
        return;
      }

      if (status === "failed" || status === "error") {
        setLoadingExport(false);
        return;
      }

      if (jobId) {
        setError("");
        pollStatus(jobId);
      } else {
        setLoadingExport(false);
        setError("Could not retrieve job ID from server response.");
      }
    } catch (err) {
      setLoadingExport(false);
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate invoice. Please try again.";
      setError(msg);
    }
  };

  const handlePageChange = (event, newPage) => {
    fetchInvoicePreview(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 0 }));
    fetchInvoicePreview(0, newLimit);
  };

  const handleReset = () => {
    clearPolling();
    setJobStatus(null);
    setError("");
    setLoadingExport(false);
    setLoadingPreview(false);
    setStartDate(null);
    setEndDate(null);
    setInvoiceData([]);
    setPagination({ page: 0, limit: 10, total: 0, totalPages: 0 });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return dayjs(date).format("DD/MM/YYYY");
  };

  // Use cumulative stats from API for date range
  const totalRevenue = summaryStats.totalWithGST;
  const totalBase = summaryStats.totalBase;
  const totalGST = summaryStats.totalGST;
  const totalCGST = invoiceData.reduce((sum, inv) => sum + (inv.cgst || 0), 0);
  const totalSGST = invoiceData.reduce((sum, inv) => sum + (inv.sgst || 0), 0);
  const totalIGST = invoiceData.reduce((sum, inv) => sum + (inv.igst || 0), 0);
  const intraStateCount = invoiceData.filter((inv) => (inv.cgst || 0) > 0).length;
  const interStateCount = invoiceData.filter((inv) => (inv.igst || 0) > 0).length;

  // Derive Financial Year and Quarter from selected date range
  const getTaxPeriod = () => {
    if (!startDate || !endDate) return null;
    const s = dayjs(startDate);
    const e = dayjs(endDate);
    const fyYear = s.month() >= 3 ? s.year() : s.year() - 1; // FY starts April
    const fy = `FY ${fyYear}-${String(fyYear + 1).slice(-2)}`;
    const month = s.month(); // 0-indexed
    let quarter = "Q1";
    if (month >= 3 && month <= 5) quarter = "Q1 (Apr–Jun)";
    else if (month >= 6 && month <= 8) quarter = "Q2 (Jul–Sep)";
    else if (month >= 9 && month <= 11) quarter = "Q3 (Oct–Dec)";
    else quarter = "Q4 (Jan–Mar)";
    return { fy, quarter, from: s.format("DD/MM/YYYY"), to: e.format("DD/MM/YYYY") };
  };
  const taxPeriod = getTaxPeriod();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.default", minHeight: "100vh" }}>

      {/* ── Hero Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #35035d 0%, #6a0dad 60%, #9c27b0 100%)",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 8px 32px rgba(53,3,93,0.3)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -60,
            right: 80,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.15)",
              width: 56,
              height: 56,
              backdropFilter: "blur(4px)",
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 30, color: "#fff" }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", letterSpacing: 0.3 }}>
              Invoice Reports
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
              Preview and export invoices by date range
            </Typography>
          </Box>
        </Box>

        {invoiceData.length > 0 && (
          <Chip
            icon={<ReceiptIcon sx={{ color: "#fff !important" }} />}
            label={`${pagination.total} Invoices Found`}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              backdropFilter: "blur(4px)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
        )}
      </Box>

      {/* ── Filter Card ── */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: isDark ? "none" : "0 2px 12px rgba(53,3,93,0.08)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={2.5}>
            <CalendarMonthIcon sx={{ color: "#6a0dad", fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              Select Date Range
            </Typography>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
            >
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                maxDate={endDate || undefined}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: 190, borderRadius: 2 },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate || undefined}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: 190, borderRadius: 2 },
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={() => fetchInvoicePreview(0)}
                disabled={loadingPreview || !startDate || !endDate}
                startIcon={
                  loadingPreview ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
                sx={{
                  background: "linear-gradient(135deg, #35035d, #6a0dad)",
                  "&:hover": { background: "linear-gradient(135deg, #4a0480, #8b00d6)" },
                  "&:disabled": { background: "#ccc" },
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#fff",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: "0 4px 14px rgba(106,13,173,0.4)",
                }}
              >
                {loadingPreview ? "Fetching..." : "Fetch Report"}
              </Button>

              {invoiceData.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleExport}
                  disabled={loadingExport}
                  startIcon={
                    loadingExport ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    color: isDark ? "#ce93d8" : "#35035d",
                    borderColor: isDark ? "#ce93d8" : "#35035d",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    "&:hover": {
                      bgcolor: isDark ? "rgba(106,13,173,0.12)" : "#f8f0ff",
                      borderColor: "#6a0dad",
                    },
                  }}
                >
                  {loadingExport ? "Generating..." : "Export ZIP"}
                </Button>
              )}

              {(jobStatus || error || invoiceData.length > 0) && (
                <Tooltip title="Reset all filters and data">
                  <IconButton
                    onClick={handleReset}
                    size="small"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: isDark ? "rgba(229,57,53,0.1)" : "#fff5f5",
                      color: "#e53935",
                      "&:hover": { bgcolor: isDark ? "rgba(229,57,53,0.2)" : "#ffebee" },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </LocalizationProvider>

          {loadingPreview && (
            <LinearProgress
              sx={{
                mt: 2,
                borderRadius: 1,
                bgcolor: isDark ? "rgba(106,13,173,0.15)" : "#e8d5f5",
                "& .MuiLinearProgress-bar": { bgcolor: "#6a0dad" },
              }}
            />
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2, borderRadius: 2 }}
              icon={<ErrorOutlineIcon />}
            >
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── Export Status Card ── */}
      {jobStatus && (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor:
              jobStatus.status === "completed" || jobStatus.status === "success"
                ? "#a5d6a7"
                : jobStatus.status === "failed" || jobStatus.status === "error"
                ? "#ef9a9a"
                : "#90caf9",
            borderLeft: "4px solid",
            borderLeftColor:
              jobStatus.status === "completed" || jobStatus.status === "success"
                ? "#4caf50"
                : jobStatus.status === "failed" || jobStatus.status === "error"
                ? "#f44336"
                : "#2196f3",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              {(jobStatus.status === "completed" || jobStatus.status === "success") ? (
                <CheckCircleIcon sx={{ color: "#4caf50" }} />
              ) : jobStatus.status === "failed" || jobStatus.status === "error" ? (
                <ErrorOutlineIcon sx={{ color: "#f44336" }} />
              ) : (
                <HourglassEmptyIcon sx={{ color: "#2196f3", animation: "pulse 2s infinite", "@keyframes pulse": { "0%": { opacity: 1 }, "50%": { opacity: 0.5 }, "100%": { opacity: 1 } } }} />
              )}
              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                {jobStatus.status === "completed" || jobStatus.status === "success"
                  ? "Export Ready"
                  : jobStatus.status === "failed" || jobStatus.status === "error"
                  ? "Export Failed"
                  : "Generating Invoice ZIP"}
              </Typography>
              <Chip
                label={jobStatus.status}
                color={
                  jobStatus.status === "completed" || jobStatus.status === "success"
                    ? "success"
                    : jobStatus.status === "failed" || jobStatus.status === "error"
                    ? "error"
                    : "info"
                }
                size="small"
                sx={{ textTransform: "capitalize", fontWeight: 600 }}
              />
              {jobStatus.id && (
                <Typography variant="caption" color="text.secondary">
                  Job ID: <strong>{jobStatus.id}</strong>
                </Typography>
              )}
            </Box>

            {jobStatus.msg && (
              <Typography variant="body2" mt={1} color="text.secondary">
                {jobStatus.msg}
              </Typography>
            )}

            {jobStatus.status === "completed" || jobStatus.status === "success" ? (
              <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }} icon={<CheckCircleIcon />}>
                Your invoice export is ready! Auto-downloading...
              </Alert>
            ) : jobStatus.status === "failed" || jobStatus.status === "error" ? (
              <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                Export failed. Please try again.
              </Alert>
            ) : (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Generating your invoice ZIP file. Please wait...
                </Typography>
                <LinearProgress
                  sx={{
                    borderRadius: 1,
                    bgcolor: "#e3f2fd",
                    "& .MuiLinearProgress-bar": { bgcolor: "#2196f3" },
                  }}
                />
              </Box>
            )}

            <Box display="flex" gap={1.5} mt={1.5} flexWrap="wrap">
              {(jobStatus.status === "completed" ||
                jobStatus.status === "success") &&
                jobStatus.downloadUrl && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => triggerDownload(jobStatus.downloadUrl)}
                  sx={{
                    background: "linear-gradient(135deg, #2e7d32, #43a047)",
                    "&:hover": { background: "linear-gradient(135deg, #1b5e20, #2e7d32)" },
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(46,125,50,0.35)",
                  }}
                >
                  Download ZIP
                </Button>
              )}

              {(jobStatus.status === "failed" || jobStatus.status === "error") && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleExport}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Try Again
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Summary Stat Cards ── */}
      {invoiceData.length > 0 && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark ? "none" : "0 2px 10px rgba(53,3,93,0.08)",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: isDark ? "rgba(106,13,173,0.2)" : "#ede7f6", width: 44, height: 44 }}>
                  <ReceiptIcon sx={{ color: "#6a0dad" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    TOTAL INVOICES
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="text.primary">
                    {summaryStats.totalInvoices}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark ? "none" : "0 2px 10px rgba(53,3,93,0.08)",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: isDark ? "rgba(156,39,176,0.2)" : "#f3e5f5", width: 44, height: 44 }}>
                  <CurrencyRupeeIcon sx={{ color: "#9c27b0" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    BASE REVENUE
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color={isDark ? "#ce93d8" : "#6a0dad"}>
                    {formatCurrency(totalBase)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark ? "none" : "0 2px 10px rgba(53,3,93,0.08)",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: isDark ? "rgba(67,160,71,0.2)" : "#e8f5e9", width: 44, height: 44 }}>
                  <CurrencyRupeeIcon sx={{ color: "#43a047" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    TOTAL WITH GST
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color={isDark ? "#81c784" : "#2e7d32"}>
                    {formatCurrency(totalRevenue)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark ? "none" : "0 2px 10px rgba(230,81,0,0.08)",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: isDark ? "rgba(230,81,0,0.2)" : "#fff3e0", width: 44, height: 44 }}>
                  <CurrencyRupeeIcon sx={{ color: "#e65100" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    GST COLLECTED
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#e65100">
                    {formatCurrency(totalGST)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* ── GST Tax Summary (for CA) ── */}
      {invoiceData.length > 0 && taxPeriod && (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: isDark ? "rgba(255,160,0,0.3)" : "#ffe082",
            boxShadow: isDark ? "none" : "0 2px 12px rgba(230,81,0,0.1)",
            bgcolor: isDark ? "rgba(255,160,0,0.06)" : "#fffde7",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2.5}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: "#fff3e0", width: 40, height: 40 }}>
                  <ReceiptIcon sx={{ color: "#e65100", fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color={isDark ? "#ffa726" : "#b45309"}>
                    GST Tax Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    For CA use — Tax filing reference (18% GST @ CGST 9% + SGST 9%)
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {/* <Chip
                  label={taxPeriod.fy}
                  size="small"
                  sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700, border: "1px solid #ffcc80" }}
                />
                <Chip
                  label={taxPeriod.quarter}
                  size="small"
                  sx={{ bgcolor: "#fce4ec", color: "#c2185b", fontWeight: 600, border: "1px solid #f48fb1" }}
                /> */}
                <Chip
                  label={`${taxPeriod.from} – ${taxPeriod.to}`}
                  size="small"
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, border: "1px solid #a5d6a7" }}
                />
              </Stack>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: isDark ? "rgba(255,160,0,0.2)" : "#ffe082" }} />

            {/* Intra-State vs Inter-State counts */}
            <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
              <Chip
                label={`${intraStateCount} Intra-State (CGST + SGST)`}
                size="small"
                sx={{ bgcolor: isDark ? "rgba(21,101,192,0.2)" : "#e3f2fd", color: isDark ? "#90caf9" : "#1565c0", fontWeight: 600, border: "1px solid", borderColor: isDark ? "rgba(144,202,249,0.3)" : "#90caf9" }}
              />
              <Chip
                label={`${interStateCount} Inter-State (IGST)`}
                size="small"
                sx={{ bgcolor: isDark ? "rgba(198,40,40,0.2)" : "#fce4ec", color: isDark ? "#ef9a9a" : "#c62828", fontWeight: 600, border: "1px solid", borderColor: isDark ? "rgba(239,154,154,0.3)" : "#ef9a9a" }}
              />
            </Stack>

            {/* Tax breakdown grid */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              flexWrap="wrap"
              divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "#ffe082" }} />}
            >
              <Box flex={1} textAlign="center" py={1} minWidth={120}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block">
                  TAXABLE VALUE
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  (Excl. GST)
                </Typography>
                <Typography variant="h6" fontWeight={800} color="text.primary">
                  {formatCurrency(totalBase)}
                </Typography>
              </Box>

              <Box flex={1} textAlign="center" py={1} minWidth={120}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block">
                  CGST @ 9%
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Central Tax
                </Typography>
                <Typography variant="h6" fontWeight={800} color={isDark ? "#90caf9" : "#1565c0"}>
                  {formatCurrency(totalCGST)}
                </Typography>
              </Box>

              <Box flex={1} textAlign="center" py={1} minWidth={120}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block">
                  SGST @ 9%
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  State Tax
                </Typography>
                <Typography variant="h6" fontWeight={800} color={isDark ? "#ce93d8" : "#6a1b9a"}>
                  {formatCurrency(totalSGST)}
                </Typography>
              </Box>

              <Box
                flex={1}
                textAlign="center"
                py={1}
                minWidth={120}
                sx={{ bgcolor: isDark ? "rgba(198,40,40,0.15)" : "#fce4ec", borderRadius: 2, border: "1px solid", borderColor: isDark ? "rgba(239,154,154,0.3)" : "#ef9a9a" }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block">
                  IGST @ 18%
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Inter-State Tax
                </Typography>
                <Typography variant="h6" fontWeight={800} color={isDark ? "#ef9a9a" : "#c62828"}>
                  {formatCurrency(totalIGST)}
                </Typography>
              </Box>

              <Box
                flex={1}
                textAlign="center"
                py={1}
                minWidth={120}
                sx={{ bgcolor: isDark ? "rgba(230,81,0,0.15)" : "#fff3e0", borderRadius: 2, border: "1px solid", borderColor: isDark ? "rgba(255,183,77,0.3)" : "#ffcc80" }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block">
                  TOTAL GST
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  CGST + SGST + IGST
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#e65100">
                  {formatCurrency(totalGST)}
                </Typography>
              </Box>

              <Box
                flex={1}
                textAlign="center"
                py={1}
                minWidth={120}
                sx={{ bgcolor: isDark ? "rgba(67,160,71,0.15)" : "#e8f5e9", borderRadius: 2, border: "1px solid", borderColor: isDark ? "rgba(129,199,132,0.3)" : "#a5d6a7" }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block">
                  GRAND TOTAL
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Incl. GST
                </Typography>
                <Typography variant="h6" fontWeight={800} color={isDark ? "#81c784" : "#2e7d32"}>
                  {formatCurrency(totalRevenue)}
                </Typography>
              </Box>
            </Stack>

            <Box mt={2} p={1.5} sx={{ bgcolor: isDark ? "rgba(255,160,0,0.08)" : "#fffbf0", borderRadius: 1.5, border: "1px dashed", borderColor: isDark ? "rgba(255,213,79,0.3)" : "#ffd54f" }}>
              <Typography variant="caption" color="text.secondary">
                ⚠️ <strong>Note for CA:</strong> Figures reflect invoices on the <strong>current page only</strong>.Use <em>Export ZIP</em> for all invoices in the full date range.
                IGST @ 18% applied for inter-state transactions; CGST 9% + SGST 9% for intra-state (UP).
                Verify state of supply per invoice before filing <strong>GSTR-1 / GSTR-3B</strong>.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Invoice Table ── */}
      {invoiceData.length > 0 && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark ? "none" : "0 2px 14px rgba(53,3,93,0.08)",
            overflow: "hidden",
          }}
        >
          <Box
            px={3}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(135deg, #9055c0 0%, rgb(167, 108, 208) 100%)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} color="#fff">
              Invoice Preview
            </Typography>
            <Chip
              label={`${pagination.total} records`}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["Invoice ID", "Customer", "Type", "Taxable Value", "CGST (9%)", "SGST (9%)", "IGST (18%)", "With GST", "Status", "Invoice", "Date"].map((h) => (
                    <TableCell
                      key={h}
                      align={["Taxable Value", "CGST (9%)", "SGST (9%)", "IGST (18%)", "With GST"].includes(h) ? "right" : "left"}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        color: "#fff !important",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        py: 1.5,
                        borderBottom: "2px solid rgba(255,255,255,0.2)",
                        backgroundColor: isDark ? "#4a0090" : "#4a0480",
                        background: isDark
                          ? "rgba(106,13,173,0.35)"
                          : "linear-gradient(135deg, #4a0480 0%, #7b1fa2 100%)",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceData.map((invoice, index) => (
                  <TableRow
                    key={invoice.transactionId || index}
                    sx={{
                      bgcolor: index % 2 === 0
                        ? "background.paper"
                        : isDark ? "rgba(225, 214, 214, 0.02)" : "#fdfbff",
                      "&:hover": {
                        bgcolor: isDark ? "rgba(106,13,173,0.12)" : "#f3e8ff",
                        transition: "background 0.15s ease",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontFamily: "monospace",
                          color: isDark ? "#ce93d8" : "#35035d",
                          fontSize: "0.8rem",
                        }}
                      >
                        {invoice.invoiceId || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {invoice.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {invoice.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.creditType}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          bgcolor: invoice.creditType === "PHC"
                            ? isDark ? "rgba(123,31,162,0.25)" : "#f3e5f5"
                            : isDark ? "rgba(57,73,171,0.25)" : "#e8eaf6",
                          color: invoice.creditType === "PHC"
                            ? isDark ? "#ce93d8" : "#7b1fa2"
                            : isDark ? "#9fa8da" : "#3949ab",
                          border: "1px solid",
                          borderColor: invoice.creditType === "PHC"
                            ? isDark ? "rgba(206,147,216,0.3)" : "#ce93d8"
                            : isDark ? "rgba(159,168,218,0.3)" : "#9fa8da",
                        }}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500} color="text.secondary">
                        {formatCurrency(invoice.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color={isDark ? "#90caf9" : "#1565c0"}>
                        {formatCurrency(invoice.cgst)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color={isDark ? "#ce93d8" : "#6a1b9a"}>
                        {formatCurrency(invoice.sgst)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={(invoice.igst || 0) > 0 ? "#c62828" : "text.disabled"}
                      >
                        {(invoice.igst || 0) > 0 ? formatCurrency(invoice.igst) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          color: isDark ? "#81c784" : "#2e7d32",
                          bgcolor: isDark ? "rgba(67,160,71,0.15)" : "#e8f5e9",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          display: "inline-block",
                        }}
                      >
                        {formatCurrency(invoice.withGST)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status}
                        size="small"
                        color={statusColor[invoice.status] || "default"}
                        sx={{ textTransform: "capitalize", fontWeight: 600, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      {invoice.invoiceLink ? (
                        <Tooltip title="View Invoice">
                          <IconButton
                            size="small"
                            href={invoice.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#6a0dad",
                              "&:hover": { bgcolor: isDark ? "rgba(106,13,173,0.15)" : "#f3e5f5" },
                            }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(invoice.date)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ borderColor: "#e0d4f5" }} />

          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={{
              "& .MuiTablePagination-toolbar": { color: isDark ? "#f5f5f5" : "#1f1f1f" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontWeight: 500,
              },
            }}
          />
        </Card>
      )}

      {/* ── Empty State (after search) ── */}
      {!loadingPreview && invoiceData.length === 0 && startDate && endDate && (
        <Card
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 3,
            border: "2px dashed",
            borderColor: isDark ? "rgba(180,120,220,0.3)" : "#d4bfec",
            textAlign: "center",
            bgcolor: "background.paper",
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: isDark ? "rgba(106,13,173,0.2)" : "#ede7f6",
              mx: "auto",
              mb: 2,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 36, color: "#9c27b0" }} />
          </Avatar>
          <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
            No Invoices Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No invoices match the selected date range. Try a wider range.
          </Typography>
        </Card>
      )}

      {/* ── Initial State ── */}
      {!startDate && !endDate && invoiceData.length === 0 && (
        <Card
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 3,
            border: "2px dashed",
            borderColor: isDark ? "rgba(180,120,220,0.3)" : "#d4bfec",
            textAlign: "center",
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: isDark
                ? "radial-gradient(circle, rgba(106,13,173,0.25), rgba(156,39,176,0.15))"
                : "linear-gradient(135deg, #ede7f6, #f3e5f5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 40, color: "#6a0dad", opacity: isDark ? 0.8 : 0.7 }} />
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
            Select a Date Range to Get Started
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={380} mx="auto">
            Choose a start and end date above to preview invoices and export them as a ZIP archive.
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default Invoices;
